// frontend/src/widgets/FloodIntelligenceWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-06
// Updated:      2026-09-08 09:52 BST
//
// Description:  Flood Intelligence widget.
//               - Location pinning via LocationEditModal.
//               - Reads/writes shared pinned location so Weather and Flood widgets stay in sync.
//               - Fetches /api/flood/intelligence using settled pinned coordinates only.
//               - Resolves created locations robustly from modal callback payload or by GET /api/locations/{id}.
//               - Responsive stacked layout for narrow/mobile widths.
//               - Cancels stale flood requests when location changes/unmounts.
//               - Reuses short-lived client cache per coordinate to avoid repeated expensive loads.
//               - Surfaces request timing/source so slow backend behaviour is visible rather than opaque.
//               - Expands the mobile ReactGridLayout host to the widget's natural content height.
//
// Performance note (2026-09-08):
//   Request-timing evidence shows /api/flood/intelligence can take several seconds
//   even when successful. This widget therefore avoids duplicate/remount fetches,
//   but the underlying endpoint/upstream aggregation remains a separate backend
//   performance work item.

import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";
import LocationEditModal from "../components/LocationEditModal.jsx";

const SHARED_LOCATION_KEY = "jobsPinnedLocation";
const SHARED_LOCATION_EVENT = "jobs-pinned-location-changed";
const MOBILE_BREAKPOINT = 768;
const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;
const floodClientCache = new Map();

const isFiniteNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n);
};

const fmtNum = (n, dp = 1) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(dp);
};

const safeText = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s || fallback;
};

const formatApiError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data == null) return status ? `${status} — Request failed` : err?.message || "Request failed";
  if (typeof data === "string") return status ? `${status} — ${data}` : data;

  const detail = data?.detail ?? data?.message ?? data;
  try {
    const text = typeof detail === "string" ? detail : JSON.stringify(detail);
    return status ? `${status} — ${text}` : text;
  } catch {
    return status ? `${status} — Error` : "Error";
  }
};

const formatDateTimeGB = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });
  } catch {
    return "—";
  }
};

const formatSigned = (value, digits = 3) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
    return "—";
  }
  const n = Number(value);
  if (n === 0) return `0.${"0".repeat(Math.max(0, digits))}`;
  return `${n > 0 ? "+" : ""}${n.toFixed(digits)}`;
};

const formatValue = (value, unit = "", digits = 3) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(digits)}${unit ? ` ${unit}` : ""}`;
};

const formatRain = (value) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(1)} mm`;
};

const riskToneMap = {
  low: {
    bg: "rgba(16, 185, 129, 0.10)",
    border: "rgba(16, 185, 129, 0.30)",
    text: "#047857",
    label: "Low",
  },
  medium: {
    bg: "rgba(245, 158, 11, 0.10)",
    border: "rgba(245, 158, 11, 0.30)",
    text: "#b45309",
    label: "Medium",
  },
  high: {
    bg: "rgba(249, 115, 22, 0.12)",
    border: "rgba(249, 115, 22, 0.35)",
    text: "#c2410c",
    label: "High",
  },
  critical: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.35)",
    text: "#b91c1c",
    label: "Critical",
  },
  unknown: {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.25)",
    text: "#4b5563",
    label: "Unknown",
  },
};

const trendTone = (trendRaw) => {
  const trend = String(trendRaw || "").trim().toLowerCase();
  if (trend === "rising") {
    return {
      bg: "rgba(239, 68, 68, 0.10)",
      border: "rgba(239, 68, 68, 0.25)",
      text: "#b91c1c",
    };
  }
  if (trend === "falling") {
    return {
      bg: "rgba(59, 130, 246, 0.10)",
      border: "rgba(59, 130, 246, 0.25)",
      text: "#1d4ed8",
    };
  }
  if (trend === "stable" || trend === "dry") {
    return {
      bg: "rgba(107, 114, 128, 0.10)",
      border: "rgba(107, 114, 128, 0.25)",
      text: "#4b5563",
    };
  }
  return {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.25)",
    text: "#4b5563",
  };
};

const severityRank = (severityLevel, severityText) => {
  if (Number.isFinite(Number(severityLevel))) return Number(severityLevel);
  const txt = String(severityText || "").toLowerCase();
  if (txt.includes("severe flood warning")) return 1;
  if (txt.includes("flood warning")) return 2;
  if (txt.includes("flood alert")) return 3;
  if (txt.includes("warning no longer")) return 4;
  return 999;
};

const pickBestAlert = (alerts) => {
  if (!Array.isArray(alerts) || alerts.length === 0) return null;
  return alerts
    .slice()
    .sort((a, b) => {
      const ar = severityRank(a?.severity_level, a?.severity);
      const br = severityRank(b?.severity_level, b?.severity);
      if (ar !== br) return ar - br;
      return new Date(b?.time_message_changed || 0) - new Date(a?.time_message_changed || 0);
    })[0];
};

const chipStyle = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 12px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 900,
  background: tone.bg,
  border: `1px solid ${tone.border}`,
  color: tone.text,
});

const readSharedLocation = () => {
  try {
    const raw = localStorage.getItem(SHARED_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lon = Number(parsed?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return {
      name: parsed?.name || "Pinned Location",
      lat,
      lon,
    };
  } catch {
    return null;
  }
};

const broadcastSharedLocation = (loc) => {
  window.dispatchEvent(
    new CustomEvent(SHARED_LOCATION_EVENT, {
      detail: {
        name: loc?.name || "Pinned Location",
        lat: Number(loc?.lat),
        lon: Number(loc?.lon),
        updated_at: new Date().toISOString(),
      },
    })
  );
};

const writeSharedLocation = (loc) => {
  const payload = {
    name: loc?.name || "Pinned Location",
    lat: Number(loc?.lat),
    lon: Number(loc?.lon),
    updated_at: new Date().toISOString(),
  };

  try {
    localStorage.setItem(SHARED_LOCATION_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }

  broadcastSharedLocation(payload);
};


const floodCacheKey = (loc) => {
  if (!isFiniteNumber(loc?.lat) || !isFiniteNumber(loc?.lon)) return null;
  return `${Number(loc.lat).toFixed(6)}:${Number(loc.lon).toFixed(6)}`;
};

const readFloodCache = (loc) => {
  const key = floodCacheKey(loc);
  if (!key) return null;

  const cached = floodClientCache.get(key);
  if (!cached?.data || !Number.isFinite(Number(cached?.storedAt))) return null;

  return {
    data: cached.data,
    storedAt: Number(cached.storedAt),
    ageMs: Math.max(0, Date.now() - Number(cached.storedAt)),
  };
};

const writeFloodCache = (loc, data) => {
  const key = floodCacheKey(loc);
  if (!key || !data) return;

  floodClientCache.set(key, {
    storedAt: Date.now(),
    data,
  });
};

const sameLocation = (a, b) =>
  Number(a?.lat) === Number(b?.lat) &&
  Number(a?.lon) === Number(b?.lon) &&
  String(a?.name || "") === String(b?.name || "");

const extractLocationFromAny = (obj) => {
  if (!obj || typeof obj !== "object") return null;

  const lat =
    obj?.lat ??
    obj?.latitude ??
    obj?.location?.lat ??
    obj?.location?.latitude ??
    null;

  const lon =
    obj?.lon ??
    obj?.lng ??
    obj?.longitude ??
    obj?.location?.lon ??
    obj?.location?.lng ??
    obj?.location?.longitude ??
    null;

  const name =
    obj?.name ??
    obj?.label ??
    obj?.title ??
    obj?.location?.name ??
    "Pinned Location";

  if (!isFiniteNumber(lat) || !isFiniteNumber(lon)) return null;

  return {
    name: String(name || "Pinned Location"),
    lat: Number(lat),
    lon: Number(lon),
  };
};

const resolveCreatedLocation = async (created) => {
  const direct = extractLocationFromAny(created);
  if (direct) return direct;

  const id = created?.id ?? created?.location_id ?? created?.location?.id ?? null;
  if (!id) return null;

  const r = await api.get(`/locations/${id}`);
  return extractLocationFromAny(r?.data);
};

const FloodIntelligenceWidget = () => {
  const { theme } = useTheme();

  const [openPicker, setOpenPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const initialSharedLocation =
    readSharedLocation() || {
      name: "Pinned Location",
      lat: 51.5074,
      lon: -0.1278,
    };

  const initialFloodCache = readFloodCache(initialSharedLocation);

  const [resp, setResp] = useState(() => initialFloodCache?.data || null);
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 900);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [dataSource, setDataSource] = useState(() =>
    initialFloodCache?.data ? "client-cache" : "none"
  );
  const [lastDurationMs, setLastDurationMs] = useState(null);
  const [slowRequest, setSlowRequest] = useState(false);

  const rootRef = useRef(null);
  const abortRef = useRef(null);
  const requestSeqRef = useRef(0);

  const [loc, setLoc] = useState(
    () => initialSharedLocation
  );

  const headerFont = useMemo(() => theme?.font_header || "var(--font-header, Arial)", [theme]);
  const bodyFont = useMemo(() => theme?.font_body || "var(--font-body, Arial)", [theme]);
  const accent = useMemo(() => theme?.accent_color || "var(--accent-color, #ff7a00)", [theme]);

  const btnBase = {
    border: "none",
    borderRadius: 999,
    padding: "10px 16px",
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: bodyFont,
    transition: "transform 0.06s ease, filter 0.15s ease",
    userSelect: "none",
    minHeight: 44,
    width: isMobile ? "100%" : "auto",
  };

  const mkBtnHandlers = (normal, hover, click) => ({
    onMouseEnter: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.backgroundColor = normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
    onMouseDown: (e) => {
      e.currentTarget.style.backgroundColor = click || hover || normal;
      e.currentTarget.style.transform = "translateY(1px)";
    },
    onMouseUp: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
  });

  const actionBtnStyle = useMemo(() => {
    const normal = theme?.button_action?.normal || "#1f7a8c";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont, isMobile]);

  const generalBtnStyle = useMemo(() => {
    const normal = theme?.button_general?.normal || "#222";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont, isMobile]);

  const actionHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_action?.normal || "#1f7a8c",
        theme?.button_action?.hover || "#256f7f",
        theme?.button_action?.click || "#1b5e6c"
      ),
    [theme]
  );

  const generalHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_general?.normal || "#222",
        theme?.button_general?.hover || "#333",
        theme?.button_general?.click || "#111"
      ),
    [theme]
  );

  const inputStyle = useMemo(
    () => ({
      height: 44,
      padding: "10px 14px",
      border: "1px solid rgba(0,0,0,0.12)",
      borderRadius: 16,
      width: "100%",
      background: "#fff",
      boxSizing: "border-box",
      fontFamily: bodyFont,
      fontSize: isMobile ? 16 : 14,
      outline: "none",
    }),
    [bodyFont, isMobile]
  );

  useEffect(() => {
    const onResize = () => {
      setIsNarrow(window.innerWidth < 900);
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!busy) {
      setSlowRequest(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSlowRequest(true);
    }, 2500);

    return () => window.clearTimeout(timer);
  }, [busy]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const fetchFlood = async ({ cacheBust = false, force = false } = {}) => {
    if (!isFiniteNumber(loc?.lat) || !isFiniteNumber(loc?.lon)) {
      setResp(null);
      setDataSource("none");
      setError("Pinned location is invalid.");
      return;
    }

    const currentLoc = {
      name: loc?.name || "Pinned Location",
      lat: Number(loc.lat),
      lon: Number(loc.lon),
    };

    const cached = readFloodCache(currentLoc);

    if (!cacheBust && !force && cached?.data && cached.ageMs <= CLIENT_CACHE_TTL_MS) {
      setResp(cached.data);
      setDataSource("client-cache");
      setLastDurationMs(0);
      setError("");
      setBusy(false);
      return;
    }

    if (!cacheBust && cached?.data) {
      setResp(cached.data);
      setDataSource("stale-cache");
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeqRef.current;
    const startedAt =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();

    setBusy(true);
    setError("");

    try {
      const r = await api.get("/flood/intelligence", {
        params: {
          lat: currentLoc.lat,
          lon: currentLoc.lon,
          persist: true,
          ...(cacheBust ? { cache_bust: 1 } : {}),
        },
        signal: controller.signal,
      });

      if (seq !== requestSeqRef.current) return;

      const next = r?.data || null;
      setResp(next);
      setDataSource(cacheBust ? "fresh-cache-bust" : "network");
      writeFloodCache(currentLoc, next);

      const finishedAt =
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
      setLastDurationMs(Math.max(0, Math.round(finishedAt - startedAt)));
    } catch (e) {
      if (
        controller.signal.aborted ||
        e?.code === "ERR_CANCELED" ||
        e?.name === "CanceledError"
      ) {
        return;
      }

      if (seq !== requestSeqRef.current) return;

      if (!cached?.data) {
        setResp(null);
        setDataSource("none");
      }
      setError(formatApiError(e));
    } finally {
      if (seq === requestSeqRef.current) {
        setBusy(false);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    }
  };

  const lastCoordRef = useRef("");
  useEffect(() => {
    const key = `${fmtNum(loc?.lat, 6)}|${fmtNum(loc?.lon, 6)}`;
    if (key === lastCoordRef.current) return;
    lastCoordRef.current = key;
    fetchFlood({ cacheBust: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc?.lat, loc?.lon]);

  useEffect(() => {
    const shared = readSharedLocation();
    if (shared) {
      setLoc((prev) => (sameLocation(prev, shared) ? prev : shared));
    }

    const onStorage = (event) => {
      if (event.key !== SHARED_LOCATION_KEY) return;
      const sharedLoc = readSharedLocation();
      if (!sharedLoc) return;
      setLoc((prev) => (sameLocation(prev, sharedLoc) ? prev : sharedLoc));
    };

    const onSharedLocation = (event) => {
      const nextLoc = event?.detail;
      if (!nextLoc || !isFiniteNumber(nextLoc?.lat) || !isFiniteNumber(nextLoc?.lon)) return;
      setLoc((prev) => (sameLocation(prev, nextLoc) ? prev : nextLoc));
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(SHARED_LOCATION_EVENT, onSharedLocation);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SHARED_LOCATION_EVENT, onSharedLocation);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [isMobile, busy, error, resp, openPicker, slowRequest]);

  const pinnedLabel = useMemo(() => {
    if (loc?.name && String(loc.name).trim()) return String(loc.name).trim();
    return "Pinned Location";
  }, [loc]);

  const liveBadge = useMemo(() => {
    const ok = !busy && !error && resp?.ok;
    const bg = ok ? "#e8f5e9" : error ? "#ffebee" : "#e3f2fd";
    const bd = ok ? "#c8e6c9" : error ? "#ffcdd2" : "#bbdefb";
    const fg = ok ? "#1b5e20" : error ? "#b71c1c" : "#0d47a1";
    const text = ok ? "Live" : error ? "Error" : busy ? "Loading" : "Idle";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 900,
          background: bg,
          border: `1px solid ${bd}`,
          color: fg,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 99, background: fg, display: "inline-block" }} />
        {text}
      </span>
    );
  }, [busy, error, resp]);

  const riskLevelRaw = String(resp?.risk_level || "unknown").toLowerCase();
  const riskTone = riskToneMap[riskLevelRaw] || riskToneMap.unknown;
  const bestAlert = pickBestAlert(resp?.official_alerts);
  const station = Array.isArray(resp?.stations) && resp.stations.length ? resp.stations[0] : null;
  const rainfall = resp?.rainfall || null;
  const tide = resp?.tide?.used ? resp.tide : null;

  return (
    <div
      ref={rootRef}
      style={{
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 16,
        background: "#fff",
        padding: isMobile ? 12 : 16,
        fontFamily: bodyFont,
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: isMobile ? "visible" : "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: isNarrow ? "stretch" : "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          flexDirection: isNarrow ? "column" : "row",
        }}
      >
        <div>
          <div style={{ fontFamily: headerFont, fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1.1 }}>
            Flood Intelligence
          </div>
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.55)", marginTop: 4 }}>
            Live flood-risk intelligence for pinned location
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            justifyContent: isNarrow ? "flex-start" : "flex-end",
            width: isMobile ? "100%" : "auto",
          }}
        >
          {resp?.risk_level ? <span style={chipStyle(riskTone)}>{riskTone.label} Risk</span> : liveBadge}
          <button
            type="button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => fetchFlood({ cacheBust: false, force: true })}
            disabled={busy}
          >
            Refresh
          </button>
        </div>
      </div>

      <div style={{ height: 1, background: "rgba(0,0,0,0.08)", marginTop: 14, marginBottom: 14 }} />

      {(busy || dataSource !== "none" || lastDurationMs !== null) && (
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 12,
            padding: "9px 11px",
            borderRadius: 12,
            background: "rgba(0,0,0,0.025)",
            border: "1px solid rgba(0,0,0,0.06)",
            fontSize: 12,
            color: "rgba(0,0,0,0.62)",
          }}
        >
          <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {busy
              ? slowRequest
                ? "Flood intelligence is still loading. Cached data remains visible where available."
                : "Refreshing flood intelligence…"
              : dataSource === "client-cache"
              ? "Showing recent client-cached flood intelligence."
              : dataSource === "stale-cache"
              ? "Showing cached flood intelligence while a fresh request completes."
              : dataSource === "fresh-cache-bust"
              ? "Fresh upstream flood intelligence loaded."
              : "Flood intelligence loaded from the backend."}
          </div>

          {lastDurationMs !== null && (
            <div style={{ fontWeight: 900, whiteSpace: "nowrap" }}>
              {lastDurationMs === 0
                ? "No network wait"
                : `Request: ${(lastDurationMs / 1000).toFixed(2)} s`}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          borderRadius: 18,
          background: "rgba(0,0,0,0.03)",
          border: "1px solid rgba(0,0,0,0.06)",
          padding: 14,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isNarrow ? "1fr" : "1fr 160px 160px",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Pinned Location
            </div>
            <input style={inputStyle} value={pinnedLabel} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Latitude
            </div>
            <input style={inputStyle} value={fmtNum(loc?.lat, 6)} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Longitude
            </div>
            <input style={inputStyle} value={fmtNum(loc?.lon, 6)} readOnly disabled />
          </div>
        </div>

        <div
          style={{
            display: isMobile ? "grid" : "flex",
            gridTemplateColumns: isMobile ? "1fr" : undefined,
            gap: 10,
            flexWrap: "wrap",
            marginTop: 12,
          }}
        >
          <button type="button" style={generalBtnStyle} {...generalHandlers} onClick={() => setOpenPicker(true)}>
            Pick Location (Map)
          </button>

          <button
            type="button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => fetchFlood({ cacheBust: true, force: true })}
            disabled={busy}
          >
            Cache Bust
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          borderRadius: 22,
          border: "1px solid rgba(0,0,0,0.08)",
          background: "#fff",
          padding: 16,
          boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
        }}
      >
        {error ? (
          <div style={{ color: "#b71c1c", fontWeight: 900, fontSize: 12, overflowWrap: "anywhere" }}>{error}</div>
        ) : !resp ? (
          <div style={{ color: "rgba(0,0,0,0.55)", fontWeight: 900, fontSize: 12 }}>
            No flood intelligence loaded yet.
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isNarrow ? "1fr" : "minmax(0, 1fr) 260px",
                gap: 12,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  borderRadius: 18,
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Summary</div>
                <div
                  style={{
                    fontSize: isNarrow ? 18 : 22,
                    fontWeight: 900,
                    color: "rgba(0,0,0,0.82)",
                    marginTop: 6,
                    lineHeight: 1.25,
                  }}
                >
                  {safeText(resp?.summary, "No active flood summary")}
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                  <span style={chipStyle(riskTone)}>Risk: {safeText(resp?.risk_level, "unknown")}</span>
                  <span
                    style={chipStyle({
                      bg: "rgba(99, 102, 241, 0.08)",
                      border: "rgba(99, 102, 241, 0.22)",
                      text: "#4338ca",
                    })}
                  >
                    Confidence: {safeText(resp?.confidence, "unknown")}
                  </span>
                  {Number.isFinite(Number(resp?.risk_score)) && (
                    <span
                      style={chipStyle({
                        bg: "rgba(17, 24, 39, 0.06)",
                        border: "rgba(17, 24, 39, 0.12)",
                        text: "#111827",
                      })}
                    >
                      Score: {Number(resp.risk_score)}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Meta</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: "rgba(0,0,0,0.82)", marginTop: 4 }}>
                  {formatDateTimeGB(resp?.generated_at)}
                </div>
                <div style={{ marginTop: 10, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Search radius: {Number.isFinite(Number(resp?.radius_km)) ? `${Number(resp.radius_km)} km` : "—"}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Alerts returned: {Array.isArray(resp?.official_alerts) ? resp.official_alerts.length : 0}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Stations returned: {Array.isArray(resp?.stations) ? resp.stations.length : 0}
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns: isNarrow ? "1fr" : "repeat(2, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Official Alert</div>
                {bestAlert ? (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "rgba(0,0,0,0.82)", marginTop: 6 }}>
                      {safeText(bestAlert?.severity)}
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.70)", marginTop: 6, lineHeight: 1.45 }}>
                      {safeText(bestAlert?.description)}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.50)", marginTop: 8 }}>
                      Updated: {formatDateTimeGB(bestAlert?.time_message_changed)}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)", marginTop: 6 }}>
                    No official alert currently returned.
                  </div>
                )}
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Nearest Water Level</div>
                {station ? (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 900, color: "rgba(0,0,0,0.82)", marginTop: 6 }}>
                      {safeText(station?.label)}
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                      <span style={chipStyle(trendTone(station?.trend))}>Trend: {safeText(station?.trend)}</span>
                      <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", alignSelf: "center" }}>
                        {Number.isFinite(Number(station?.distance_km))
                          ? `${Number(station.distance_km).toFixed(2)} km away`
                          : "Distance unknown"}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.70)", marginTop: 8 }}>
                      Latest: {formatValue(station?.latest_value, station?.unit, 3)}
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.70)", marginTop: 4 }}>
                      Δ 6h: {formatSigned(station?.delta_6h, 3)} {station?.unit ? station.unit : ""}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.50)", marginTop: 8 }}>
                      Sampled: {formatDateTimeGB(station?.latest_time)}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)", marginTop: 6 }}>
                    No nearby water-level station returned.
                  </div>
                )}
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Rainfall</div>
                {rainfall ? (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                      <span style={chipStyle(trendTone(rainfall?.trend))}>{safeText(rainfall?.trend)}</span>
                      <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", alignSelf: "center" }}>
                        {Number.isFinite(Number(rainfall?.distance_km))
                          ? `${Number(rainfall.distance_km).toFixed(2)} km away`
                          : "Distance unknown"}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.70)", marginTop: 8 }}>
                      Last 6h: {formatRain(rainfall?.total_last_6h_mm)}
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.70)", marginTop: 4 }}>
                      Last 24h: {formatRain(rainfall?.total_last_24h_mm)}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.50)", marginTop: 8 }}>
                      Station: {safeText(rainfall?.station_id)}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)", marginTop: 6 }}>
                    No rainfall summary returned.
                  </div>
                )}
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Tide</div>
                {tide ? (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                      <span style={chipStyle(trendTone(tide?.trend))}>Trend: {safeText(tide?.trend)}</span>
                      <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", alignSelf: "center" }}>
                        {Number.isFinite(Number(tide?.distance_km))
                          ? `${Number(tide.distance_km).toFixed(2)} km away`
                          : "Distance unknown"}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.70)", marginTop: 8 }}>
                      Latest: {formatValue(tide?.latest_value, tide?.unit, 3)}
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.70)", marginTop: 4 }}>
                      Δ 6h: {formatSigned(tide?.delta_6h, 3)} {tide?.unit ? tide.unit : ""}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.50)", marginTop: 8 }}>
                      Sampled: {formatDateTimeGB(tide?.latest_time)}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)", marginTop: 6 }}>
                    No tide data relevant for this location.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <LocationEditModal
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        onCreated={async (created) => {
          try {
            const resolved = await resolveCreatedLocation(created);
            if (!resolved) {
              setOpenPicker(false);
              setError("Location was created, but coordinates could not be resolved from the response.");
              return;
            }
            setLoc(resolved);
            writeSharedLocation(resolved);
            setOpenPicker(false);
          } catch (e) {
            setOpenPicker(false);
            setError(formatApiError(e));
          }
        }}
        initial={{
          name: loc?.name,
          lat: loc?.lat,
          lon: loc?.lon,
          timezone: "Europe/London",
          country: "United Kingdom",
        }}
      />
    </div>
  );
};

export default FloodIntelligenceWidget;